/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: feature lookup, alias handling, predicates, visibility.
// Source: cardiac_report_v0_5_6_dev_2.html lines 2596-2607 (findFeatureByName),
// 3346-3354, 3420-3423 (predicates), 2335-2337 (currentHeartRateBpm), 4941-4954
// (featureVisibleForReport/reportFeatures), 2609-2646 (baseline builders),
// 4974-4979 (sourceIdForFeed).
// Deviations: `data.features` global -> explicit `features` argument;
// view/admin globals -> explicit options.

import { parseLeadingNumber } from "./parse"

export function isCardiacCycleTimingFeature(featureOrName) {
  const name = typeof featureOrName === "string" ? featureOrName : featureOrName && featureOrName.name
  return name === "SYS:DIA Ratio" || name === "Cardiac Cycle Timing" || name === "Systolic/Diastolic Timing"
}

export function isSysDiaDurationFeature(featureOrName) {
  const name = typeof featureOrName === "string" ? featureOrName : featureOrName && featureOrName.name
  return name === "SYS:DIA Duration" || name === "Diastolic Duration" || name === "Systolic/Diastolic Duration"
}

export function isRhythmRegularityFeature(featureOrName) {
  const name = typeof featureOrName === "string" ? featureOrName : featureOrName && featureOrName.name
  return name === "Cycle Regularity" || name === "Rhythm Regularity"
}

export function isHeartRateFeature(featureOrName) {
  const name = typeof featureOrName === "string" ? featureOrName : featureOrName && featureOrName.name
  return name === "Heart Rate"
}

const finiteNumber = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))

/**
 * The heart-rate range a Heart Rate card shows as two markers (Heart Report
 * v0.5.8 at cardiac-panel-v4 git 31d729a): the clinician's range, else the
 * accepted evidence's, as {min, max} in bpm; null for any other card, once
 * the clinician replaced the range with a single value, or when the range is
 * a single value (a degenerate range shows as one marker, unlike upstream's
 * two coincident dots).
 */
/** Whether the accepted evidence behind a Heart Rate card was a range (two markers), not one value. */
export function hasAcceptedHeartRateRange(feature) {
  if (!isHeartRateFeature(feature)) return false
  const range = feature.sourceRangeBpm || (feature.candidateEvidenceMeta && feature.candidateEvidenceMeta.rangeBpm)
  return !!range && finiteNumber(range.min) && finiteNumber(range.max) && Number(range.min) < Number(range.max)
}

export function heartRateRangeOf(feature) {
  if (!isHeartRateFeature(feature) || feature.clinicianAssessmentMode === "single") return null
  const range =
    feature.clinicianRangeBpm ||
    feature.sourceRangeBpm ||
    (feature.candidateEvidenceMeta && feature.candidateEvidenceMeta.rangeBpm) ||
    null
  if (!range || !finiteNumber(range.min) || !finiteNumber(range.max)) return null
  const min = Math.min(Number(range.min), Number(range.max))
  const max = Math.max(Number(range.min), Number(range.max))
  return min < max ? { min, max } : null
}

/** "70–144 bpm", each end at one decimal at most (upstream's formatting). */
export function formatHeartRateRange(range) {
  const end = (value) => Number(Number(value).toFixed(1))
  return `${end(range.min)}–${end(range.max)} bpm`
}

export function findFeatureByName(features, name) {
  const aliases =
    name === "SYS:DIA Ratio" || name === "Cardiac Cycle Timing" || name === "Systolic/Diastolic Timing"
      ? ["SYS:DIA Ratio", "Cardiac Cycle Timing", "Systolic/Diastolic Timing"]
      : name === "Cycle Regularity" || name === "Rhythm Regularity"
      ? ["Cycle Regularity", "Rhythm Regularity"]
      : name === "SYS:DIA Duration" || name === "Diastolic Duration" || name === "Systolic/Diastolic Duration"
      ? ["SYS:DIA Duration", "Diastolic Duration", "Systolic/Diastolic Duration"]
      : name === "Structural Sound" || name === "Structural Sound Summary" || name === "Structural Sound Detail"
      ? ["Structural Sound", "Structural Sound Summary", "Structural Sound Detail"]
      : [name]
  return (features || []).find((f) => aliases.includes(f.name))
}

export function currentHeartRateBpm(features) {
  const feature = findFeatureByName(features, "Heart Rate")
  return parseLeadingNumber(feature && feature.observed)
}

// viewMode: 'patient' | 'provider'; adminMode: boolean
// Clinical review (adminMode) shows every standard card; the report views
// show only the cards included in the report. A card with no inclusion state
// comes from a document saved before cards carried one and shows as it did.
export function featureVisibleForReport(feature, { adminMode = false, viewMode = "patient" } = {}) {
  if (!feature) return false
  if (feature.type === "signal" || feature.name === "Signal Quality") return false
  if (feature.isHidden && !adminMode) return false
  const candidateTopLevelExclusions = ["Timing Summary", "Structural Sound Detail", "S1/S2 Intensity"]
  if (candidateTopLevelExclusions.includes(feature.name)) return false
  if (adminMode) return true
  if (feature.reportInclusionStatus && feature.reportInclusionStatus !== "accepted") return false
  if (viewMode === "provider") return feature.showToProvider !== false
  return feature.showToPatient !== false
}

export function reportFeatures(features, options) {
  return (features || []).filter((feature) => featureVisibleForReport(feature, options))
}

// A source's feed id: its explicit `sourceId` ("pcg_viewer", "act",
// "beataware"), else a match on its name for documents saved before sources
// carried one.
export function sourceIdForFeed(source) {
  const explicitId = source && source.sourceId
  if (explicitId)
    return String(explicitId)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
  const name = String((source && source.name) || "").toLowerCase()
  if (name.includes("act")) return "act"
  if (name.includes("beat")) return "beataware"
  if (name.includes("pcg")) return "pcg_viewer"
  return name.replace(/[^a-z0-9]+/g, "_") || "unknown_feed"
}

const copyRange = (range) => (range ? { ...range } : null)
const numberOrNull = (value) => (finiteNumber(value) ? Number(value) : null)

/**
 * What a timing card derives from the recordings it includes: the records
 * themselves (each with whether it is ticked), the value range they span on
 * a SYS:DIA card, and the representative medians the marker sits at. Copied
 * into every baseline so Cancel and rewind put the selection back with the
 * values, and absent (an empty list) on a card without per-recording data.
 */
export function timingSelectionSnapshot(feature) {
  return {
    timingSourceRecordings: Array.isArray(feature.timingSourceRecordings)
      ? feature.timingSourceRecordings.map((source) => ({ ...source }))
      : [],
    clinicianRatioRange: copyRange(feature.clinicianRatioRange),
    clinicianSysDiaDurationRange: copyRange(feature.clinicianSysDiaDurationRange),
    representativeSdRatio: numberOrNull(feature.representativeSdRatio),
    representativeSysMs: numberOrNull(feature.representativeSysMs),
    representativeDiaMs: numberOrNull(feature.representativeDiaMs),
  }
}

/**
 * Puts a timing card's selection and derived ranges back from a baseline.
 * A field that is already absent stays absent, so that putting a card back
 * leaves its document exactly as it was.
 */
export function restoreTimingSelection(feature, baseline) {
  const snapshot = timingSelectionSnapshot(baseline || {})
  // A document from before per-recording selection has no list to put back
  if (!Array.isArray(baseline && baseline.timingSourceRecordings)) delete snapshot.timingSourceRecordings
  Object.entries(snapshot).forEach(([key, value]) => {
    if (JSON.stringify(feature[key] ?? null) !== JSON.stringify(value)) feature[key] = value
  })
}

export function buildFeatureBaseline(feature) {
  return {
    observed: feature.observed,
    status: feature.status,
    aiStatus: feature.aiStatus || "normal",
    markerPosition: feature.markerPosition,
    // The Heart Rate range and how the clinician assessed it
    clinicianAssessmentMode: feature.clinicianAssessmentMode || null,
    clinicianRangeBpm: feature.clinicianRangeBpm ? { ...feature.clinicianRangeBpm } : null,
    sourceRangeBpm: feature.sourceRangeBpm ? { ...feature.sourceRangeBpm } : null,
    mixedRange: !!feature.mixedRange,
    // The recordings a timing card includes and the values it derives from them
    ...timingSelectionSnapshot(feature),
    // The Structural Sound recordings on the card, and which the value came from
    structuralSourceRecordings: Array.isArray(feature.structuralSourceRecordings)
      ? feature.structuralSourceRecordings.map((source) => ({ ...source }))
      : [],
    sourceRecordingId: feature.sourceRecordingId || null,
    sourcePosition: feature.sourcePosition || null,
    noteToPatient: feature.noteToPatient || "",
    patientExplanationText: feature.patientExplanationText || "",
    patientResultText: feature.patientResultText || "",
    noteToDoctor: feature.noteToDoctor || "",
    sources: (feature.sources || []).map((source) => ({
      sourceId: sourceIdForFeed(source),
      name: source.name,
      reviewStatus: source.reviewStatus || "unreviewed",
      evidenceDisposition: source.evidenceDisposition || null,
      rejectReasonCode: source.rejectReasonCode || null,
      rejectReasonText: source.rejectReasonText || null,
    })),
  }
}

export function resetFeatureSourcesToBaseline(feature, baseline) {
  if (!feature || !Array.isArray(feature.sources)) return
  const baselineSources = Array.isArray(baseline && baseline.sources) ? baseline.sources : []
  const baselineById = new Map(baselineSources.map((source) => [source.sourceId || sourceIdForFeed(source), source]))
  // A field that is already absent stays absent, so that putting a card back
  // leaves its document exactly as it was
  const restore = (source, key, value) => {
    if ((source[key] ?? null) !== value) source[key] = value
  }
  feature.sources.forEach((source, index) => {
    const sourceBaseline = baselineById.get(sourceIdForFeed(source)) || baselineSources[index] || {}
    source.reviewStatus = sourceBaseline.reviewStatus || "unreviewed"
    restore(source, "evidenceDisposition", sourceBaseline.evidenceDisposition || null)
    restore(source, "rejectReasonCode", sourceBaseline.rejectReasonCode || null)
    restore(source, "rejectReasonText", sourceBaseline.rejectReasonText || null)
    if (source._pendingReason) source._pendingReason = false
  })
}

export function ensureFeatureBaselines(features) {
  ;(features || []).forEach((feature) => {
    if (!feature.importedBaseline) feature.importedBaseline = buildFeatureBaseline(feature)
  })
}
